import React from "react";
import PropTypes from "prop-types";
import {useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {BsSearch} from "react-icons/bs";
import WS from "../../../ws";
import {getGroups} from "../../../Services/campaignFunctions";
import GroupListItem, {groupLabel} from "../GroupListItem";
import {
    Container, Header, Title, SearchBar, SearchInput,
    Filters, Filter, Badge, List, State,
} from "./styles";

/**
 * Painel esquerdo da aba Inbox: lista os grupos da campanha com busca e filtros
 * All/Unread. Busca os grupos (getGroups pelo id da rota = campanha). O contador de
 * não-lidas por grupo (`unread`) inicia do backend (group.unread_count), sobe em tempo
 * real pelo WebSocket (grupo fechado) e zera quando o grupo fica selecionado.
 */
const GroupList = ({selectedId, onSelect}) => {
    const {id} = useParams();
    const {t} = useTranslation();
    const [groups, setGroups] = React.useState([]);
    const [unread, setUnread] = React.useState({}); // {[groupId]: count}
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const [filter, setFilter] = React.useState("all"); // "all" | "unread"

    // fetch dos grupos + inicialização do unread (fonte de verdade a cada troca de campanha)
    React.useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            setError(false);
            const res = await getGroups(id);
            if (!alive) return;
            // getGroups devolve o objeto de erro do axios no catch (não lança).
            if (res?.isAxiosError || res instanceof Error) {
                setError(true);
                setGroups([]);
                setUnread({});
            } else {
                // backend retorna null (não []) para campanha sem grupos -> vira [] (estado vazio).
                const list = Array.isArray(res) ? res : [];
                setGroups(list);
                setUnread(Object.fromEntries(list.map((g) => [g.id, g.unread_count || 0])));
            }
            setLoading(false);
        })();
        return () => {
            alive = false;
        };
    }, [id]);

    // tempo real: patch da última mensagem (qualquer from_me, guardado por sent_at) +
    // incremento de não-lidas (só inbound de grupo fechado).
    React.useEffect(() => {
        return WS.subscribe((msg) => {
            if (msg?.subject !== "group_message" || !msg.data) return;
            const gid = msg.data.group_id;
            const at = msg.data.sent_at;

            // prévia: só aplica se a msg tem sent_at e é MAIS NOVA que a atual do grupo.
            if (at) {
                setGroups((prev) => prev.map((g) => {
                    if (g.id !== gid) return g;
                    if (g.last_message_at && new Date(at) <= new Date(g.last_message_at)) return g;
                    return {
                        ...g,
                        last_message_at: at,
                        last_message_content: msg.data.content,
                        last_message_sender: msg.data.from?.name ?? null,
                        last_message_from_me: msg.data.from_me,
                    };
                }));
            }

            // não-lidas: só inbound (from_me=false) de grupo NÃO aberto.
            if (msg.data.from_me === false && gid !== selectedId) {
                setUnread((prev) => (gid in prev ? {...prev, [gid]: prev[gid] + 1} : prev));
            }
        });
    }, [selectedId]);

    // ao ficar selecionado, zera o badge local na hora (o backend é zerado pelo GroupConversation)
    React.useEffect(() => {
        if (selectedId == null) return;
        setUnread((prev) => (selectedId in prev && prev[selectedId] !== 0 ? {...prev, [selectedId]: 0} : prev));
    }, [selectedId]);

    const term = search.trim().toLowerCase();
    const unreadOf = (group) => unread[group.id] || 0;
    const unreadGroupsCount = groups.filter((g) => unreadOf(g) > 0).length;

    const filtered = groups.filter((group) => {
        const matchesSearch = !term || groupLabel(group).toLowerCase().includes(term);
        const matchesFilter = filter === "all" || unreadOf(group) > 0;
        return matchesSearch && matchesFilter;
    });

    // ordena por última mensagem (mais recente no topo); sem-msg (0) vão pro fim.
    const sorted = [...filtered].sort((a, b) => {
        const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
        const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
        return tb - ta;
    });

    return (
        <Container>
            <Header>
                <Title>{t("words.groups")}</Title>
            </Header>

            <SearchBar>
                <SearchInput
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t("words.search")}
                />
                <BsSearch/>
            </SearchBar>

            <Filters>
                <Filter $active={filter === "all"} onClick={() => setFilter("all")}>
                    {t("words.all")} <Badge>{groups.length}</Badge>
                </Filter>
                <Filter $active={filter === "unread"} onClick={() => setFilter("unread")}>
                    {t("words.unread")} <Badge>{unreadGroupsCount}</Badge>
                </Filter>
            </Filters>

            <List>
                {loading && <State>{t("words.loading")}</State>}
                {!loading && error && <State>{t("words.something_wrong")}</State>}
                {!loading && !error && filtered.length === 0 && (
                    <State>{t("words.no_groups")}</State>
                )}
                {!loading && !error && sorted.map((group) => (
                    <GroupListItem
                        key={group.id}
                        group={group}
                        active={group.id === selectedId}
                        unreadCount={unreadOf(group)}
                        onClick={onSelect}
                    />
                ))}
            </List>
        </Container>
    );
};

GroupList.propTypes = {
    selectedId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onSelect: PropTypes.func.isRequired,
};

export default GroupList;
